import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    Pagination,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { UserService } from '../../services/user.service';
import { UserProfileService } from '../../services/user-profile.service';
import { InstitutionService } from '../../services/institution.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import EmployeeDocuments from './EmployeeDocuments';
import EmployeeBankingDialog from './EmployeeBankingDialog';
import EmployeeEmploymentDialog from './EmployeeEmploymentDialog';
import { formatDate } from '../../utils/dateUtils';
import { IUserProfile, PREFERRED_DISBURSEMENT_LABELS } from '../../interfaces/user-profile.interface';
import { displayUsername } from '../../utils/userUtils';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface User {
    id: string;
    username: string;
    fullName?: string | null;
    roles?: { id: string; name: string }[] | null;
}

const EmployeeDocumentAdmin: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [editingUsername, setEditingUsername] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [usernameSaving, setUsernameSaving] = useState(false);
    const [institutionCode, setInstitutionCode] = useState('');
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [checkingUsername, setCheckingUsername] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isBankingDialogOpen, setIsBankingDialogOpen] = useState(false);
    const [isEmploymentDialogOpen, setIsEmploymentDialogOpen] = useState(false);
    const canManageProfile = usePermission(PERMISSIONS.PROFILE_MANAGE);
    const toast = useToast();

    useEffect(() => {
        const fetchInstitutionCode = async () => {
            try {
                const cachedCode = AuthService.getSelectedInstitutionCode();
                if (cachedCode) {
                    setInstitutionCode(cachedCode);
                    return;
                }
                const id = AuthService.getSelectedInstitution();
                const institution = await InstitutionService.getInstitutionById(id);
                setInstitutionCode(institution.code);
            } catch (err) {
                console.error('Failed to load institution code:', err);
            }
        };
        fetchInstitutionCode();
    }, []);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        fetchEmployees(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const fetchEmployees = async (page: number = 0) => {
        setLoading(true);
        try {
            const response = await UserService.getInstitutionUsers(page, 10, undefined, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements,
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load employees');
        } finally {
            setLoading(false);
        }
    };

    const usernamePrefix = institutionCode ? `${institutionCode}_` : '';

    const checkAvailability = (value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setUsernameAvailable(null);

        if (!value.trim() || !profile) {
            setCheckingUsername(false);
            return;
        }

        setCheckingUsername(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const fullUsername = usernamePrefix + value.trim();
                const available = await UserService.checkUsernameAvailable(fullUsername, profile.userId);
                setUsernameAvailable(available);
            } catch {
                setUsernameAvailable(null);
            } finally {
                setCheckingUsername(false);
            }
        }, 500);
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewUsername(value);
        checkAvailability(value);
    };

    const handleUsernameSave = async () => {
        if (!newUsername.trim() || !profile || usernameAvailable === false) return;
        setUsernameSaving(true);
        try {
            const fullUsername = usernamePrefix + newUsername.trim();
            await UserService.updateUsername(profile.userId, fullUsername);
            const updatedProfile = await UserProfileService.getProfileByUserId(profile.userId);
            setProfile(updatedProfile);
            setUsers(prev => prev.map(u =>
                u.id === profile.userId ? { ...u, username: fullUsername } : u
            ));
            setSelectedUser(prev => prev && prev.id === profile.userId ? { ...prev, username: fullUsername } : prev);
            setEditingUsername(false);
            setUsernameAvailable(null);
            toast.showSuccess('Username updated successfully');
        } catch (err) {
            toast.showApiError(err, 'Failed to update username');
        } finally {
            setUsernameSaving(false);
        }
    };

    useEffect(() => {
        if (!selectedUser) {
            setProfile(null);
            return;
        }
        setEditingUsername(false);
        setUsernameAvailable(null);
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const data = await UserProfileService.getProfileByUserId(selectedUser.id);
                setProfile(data);
            } catch (err) {
                toast.showApiError(err, 'Failed to load employee profile');
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser?.id]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchEmployees(page - 1);
    };

    const handleRowClick = (user: User) => {
        setSelectedUser(prev => prev?.id === user.id ? null : user);
    };

    return (
        <Box sx={{ maxWidth: 900, margin: '0 auto', padding: '20px' }} data-help-page="users.profiles.page">
            <Typography variant="h5" gutterBottom>Employee Profile</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }} data-help-section="users.profiles.filters">
                <TextField
                    label="Search by username"
                    data-help="users.profiles.search"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: '1 1 220px', maxWidth: 360 }}
                />
                <Typography variant="body2" color="text.secondary" data-help="users.profiles.total">
                    Total: {pageInfo.totalElements}
                </Typography>
            </Box>

            {loading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Loading...</Typography>
            ) : (
                <>
                    <ScrollableTable>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, minWidth: 640 }} data-help-section="users.profiles.table">
                        <thead>
                            <tr>
                                <th style={tableHeaderStyle} data-help="users.profiles.col.name">Name</th>
                                <th style={tableHeaderStyle} data-help="users.profiles.col.username">Username</th>
                                <th style={tableHeaderStyle} data-help="users.profiles.col.roles">Roles</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 ? (
                                <tr>
                                    <td colSpan={3} style={{ ...tableCellStyle, textAlign: 'center', color: '#888' }}>
                                        No employees found
                                    </td>
                                </tr>
                            ) : users.map(user => (
                                <tr
                                    key={user.id}
                                    onClick={() => handleRowClick(user)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedUser?.id === user.id ? '#e3f2fd' : 'transparent',
                                    }}
                                >
                                    <td style={tableCellStyle}>{user.fullName || '—'}</td>
                                    <td style={tableCellStyle}>{displayUsername(user.username)}</td>
                                    <td style={tableCellStyle}>{user.roles && user.roles.length > 0 ? user.roles.map(r => r.name).join(', ') : '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </ScrollableTable>

                    {pageInfo.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Pagination
                                data-help="users.profiles.paging"
                                count={pageInfo.totalPages}
                                page={pageInfo.currentPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {selectedUser && profile && !profileLoading && (
                <Card sx={{ mb: 3 }} data-help-section="users.profiles.card">
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Profile Information</Typography>
                            {canManageProfile && (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setIsEmploymentDialogOpen(true)}
                                        data-help="users.profiles.editEmployment"
                                    >
                                        Edit employment details
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setIsBankingDialogOpen(true)}
                                        data-help="users.profiles.editBanking"
                                    >
                                        Edit banking & disbursement
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Username</Typography>
                                {editingUsername ? (
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                data-help="users.profiles.field.newUsername"
                                                value={newUsername}
                                                onChange={handleUsernameChange}
                                                disabled={usernameSaving}
                                                autoFocus
                                                slotProps={{
                                                    input: {
                                                        startAdornment: usernamePrefix ? (
                                                            <InputAdornment position="start">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {usernamePrefix}
                                                                </Typography>
                                                            </InputAdornment>
                                                        ) : undefined,
                                                    },
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={handleUsernameSave}
                                                disabled={usernameSaving || !newUsername.trim() || usernameAvailable === false || checkingUsername}
                                                aria-label="save username"
                                                data-help="users.profiles.saveUsername"
                                            >
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => { setEditingUsername(false); setUsernameAvailable(null); }}
                                                disabled={usernameSaving}
                                                aria-label="close"
                                                data-help="users.profiles.cancelUsername"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {newUsername.trim() && !checkingUsername && usernameAvailable !== null && (
                                            <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, color: usernameAvailable ? 'success.main' : 'error.main' }}
                                            >
                                                {usernameAvailable ? 'Available' : 'Username already taken'}
                                            </Typography>
                                        )}
                                        {checkingUsername && (
                                            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                Checking...
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body1">{displayUsername(profile.username) || '-'}</Typography>
                                        <IconButton
                                            size="small"
                                            disabled={!institutionCode}
                                            title={!institutionCode ? 'Institution code unavailable' : 'Edit username'}
                                            data-help="users.profiles.editUsername"
                                            aria-label={!institutionCode ? 'Institution code unavailable' : 'Edit username'}
                                            onClick={() => {
                                                setNewUsername(displayUsername(profile.username));
                                                setEditingUsername(true);
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">First Name</Typography>
                                <Typography variant="body1">{profile.firstName || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Last Name</Typography>
                                <Typography variant="body1">{profile.lastName || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Email</Typography>
                                <Typography variant="body1">{profile.email || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Phone</Typography>
                                <Typography variant="body1">{profile.phone || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Designation</Typography>
                                <Typography variant="body1">{profile.designation || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Employee Code</Typography>
                                <Typography variant="body1">{profile.employeeCode || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Joining Date</Typography>
                                <Typography variant="body1">{profile.joiningDate ? formatDate(profile.joiningDate) : '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">User Type</Typography>
                                <Typography variant="body1">{profile.type || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Bank Name</Typography>
                                <Typography variant="body1">{profile.bankName || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Bank Account Number</Typography>
                                <Typography variant="body1">{profile.bankAccountNumber || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Bank Routing Number</Typography>
                                <Typography variant="body1">{profile.bankRoutingNumber || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Bank Branch</Typography>
                                <Typography variant="body1">{profile.bankBranch || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Mobile Banking Provider</Typography>
                                <Typography variant="body1">{profile.mobileBankingProvider || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Mobile Banking Account</Typography>
                                <Typography variant="body1">{profile.mobileBankingAccount || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Preferred Disbursement</Typography>
                                <Typography variant="body1">
                                    {profile.preferredDisbursementMethod
                                        ? (PREFERRED_DISBURSEMENT_LABELS[profile.preferredDisbursementMethod] ?? profile.preferredDisbursementMethod)
                                        : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {selectedUser && (
                <EmployeeEmploymentDialog
                    open={isEmploymentDialogOpen}
                    userId={selectedUser.id}
                    displayName={displayUsername(profile?.username || selectedUser.username)}
                    onClose={() => setIsEmploymentDialogOpen(false)}
                    onSaved={async () => {
                        try {
                            const updated = await UserProfileService.getProfileByUserId(selectedUser.id);
                            setProfile(updated);
                        } catch (err) {
                            toast.showApiError(err, 'Failed to refresh employee profile');
                        }
                    }}
                />
            )}

            {selectedUser && (
                <EmployeeBankingDialog
                    open={isBankingDialogOpen}
                    userId={selectedUser.id}
                    displayName={displayUsername(profile?.username || selectedUser.username)}
                    onClose={() => setIsBankingDialogOpen(false)}
                    onSaved={async () => {
                        try {
                            const updated = await UserProfileService.getProfileByUserId(selectedUser.id);
                            setProfile(updated);
                        } catch (err) {
                            toast.showApiError(err, 'Failed to refresh employee profile');
                        }
                    }}
                />
            )}

            {selectedUser && (
                <EmployeeDocuments userId={selectedUser.id} employeeCode={profile?.employeeCode} />
            )}

            {!selectedUser && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Click a row to view the employee's profile and documents.
                </Typography>
            )}
        </Box>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd',
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd',
};

export default EmployeeDocumentAdmin;
